import { ApiResult } from "common/models";
import { HttpClient } from "common/services/http-client.service";
import { ENV_CONFIG } from "common/config/env.config";
import { msalInstance } from "common/config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  ExtractionRequestBody,
  ExtractionResponse,
  ExtractionResult,
  UserContext
} from "../models/document-extraction.model";
import {
  ExtractionModel,
  EXTRACTION_MODEL_ENDPOINTS
} from "../enums/extraction-model.enum";
import { documentExtractionConfig } from "../config/document-extraction.config";
import { ExtractionDataProcessor } from "../utils/extraction-data-processor.util";

/**
 * Service class for handling document extraction operations
 */
export class DocumentExtractionService extends HttpClient {
  /**
   * Validates the extraction request parameters
   */
  private static validateExtractionRequest(
    currentConfig: any,
    selectedModel: string
  ): { isValid: boolean; error?: string } {
    if (!currentConfig) {
      return {
        isValid: false,
        error: "No configuration data available"
      };
    }

    if (!ExtractionDataProcessor.isValidModel(selectedModel)) {
      return {
        isValid: false,
        error: "Please select a model first"
      };
    }

    return { isValid: true };
  }

  /**
   * Builds the extraction request body from configuration and user context
   */
  private static buildExtractionRequestBody(
    currentConfig: any,
    userContext: UserContext,
    selectedModel: string
  ): ExtractionRequestBody {
    return {
      flow_id: currentConfig.flow_id,
      document_id: currentConfig.document_id,
      config_id: currentConfig.config_id,
      document_name: currentConfig.document_name,
      blob_url: currentConfig.document_link,
      model_name: selectedModel,
      blob_path:
        currentConfig.config_id +
        "/" +
        currentConfig.flow_id +
        "/" +
        currentConfig.document_name
    };
  }

  /**
   * Gets the API endpoint for the selected extraction model
   */
  private static getExtractionEndpoint(selectedModel: string): string {
    const funcAppUrl = ENV_CONFIG.GDX_FUNCAPP_API_URL;
    if (!funcAppUrl) {
      throw new Error("Function app URL is not configured");
    }
    // Return full function app URL with the reextraction endpoint
    return `${funcAppUrl}/api/reextraction`;
  }

  /**
   * Processes the raw extraction response into a standardized format
   */
  private static processExtractionResponse(data: ExtractionResponse[]): {
    extractedFields: { [key: string]: string };
    tokenCost: string | null;
    processedFields: any[];
  } {
    // Extract new fields
    const extractedFields = ExtractionDataProcessor.extractFields(data);

    // Extract token cost
    const tokenCostInfo = ExtractionDataProcessor.extractTokenCost(data);
    const tokenCost = tokenCostInfo?.cost || null;

    // Process fields for UI display
    const processedFields = ExtractionDataProcessor.processExtractionData(data);

    return {
      extractedFields,
      tokenCost,
      processedFields
    };
  }

  /**
   * Performs document re-extraction using the specified model
   */
  static async reextractDocument(
    currentConfig: any,
    selectedModel: string,
    userContext: UserContext
  ): Promise<ExtractionResult> {
    try {
      // Validate request
      const validation = this.validateExtractionRequest(
        currentConfig,
        selectedModel
      );
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.error
        };
      }

      // Build request body
      const requestBody = this.buildExtractionRequestBody(
        currentConfig,
        userContext,
        selectedModel
      );

      // Get endpoint
      const endpoint = this.getExtractionEndpoint(selectedModel);
      if (!endpoint) {
        return {
          success: false,
          error: `Unsupported model: ${selectedModel}`
        };
      }

      // Get authentication token for function app
      const request = {
        scopes: [process.env.REACT_APP_API_SCOPE || ""]
      };

      // let tokenResult;
      // try {
      //   tokenResult = await msalInstance.acquireTokenSilent(request);
      // } catch (error: any) {
      //   if (error instanceof InteractionRequiredAuthError) {
      //     tokenResult = await msalInstance.acquireTokenPopup(request);
      //   } else {
      //     throw error;
      //   }
      // }

      // Make direct fetch call to function app
      const fetchResponse = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
          // Authorization: `Bearer ${tokenResult.accessToken}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!fetchResponse.ok) {
        return {
          success: false,
          error: `HTTP error! status: ${fetchResponse.status}`
        };
      }

      const responseData = await fetchResponse.json();

      // Check if response contains a message (background re-extraction)
      if (responseData && typeof responseData === "string") {
        return {
          success: true,
          message: responseData
        };
      }

      // Check if response contains a message property
      if (
        responseData &&
        typeof responseData === "object" &&
        "message" in responseData
      ) {
        return {
          success: true,
          message: (responseData as any).message
        };
      }

      // Legacy support: If response contains array data (immediate extraction)
      if (responseData && Array.isArray(responseData)) {
        const processed = this.processExtractionResponse(responseData);
        return {
          success: true,
          data: responseData,
          tokenCost: processed.tokenCost || undefined,
          processedFields: processed.processedFields
        };
      }

      // Default success response for background processing
      return {
        success: true,
        message: "Re-extraction started in background"
      };
    } catch (error) {
      return {
        success: false,
        error: `Error re-extracting data: ${(error as Error).message}`
      };
    }
  }

  /**
   * Gets available extraction models
   */
  static getAvailableModels(): ExtractionModel[] {
    return Object.values(ExtractionModel).filter(
      (model) => model !== ExtractionModel.SELECT_MODEL
    );
  }

  /**
   * Checks if a model is supported
   */
  static isModelSupported(model: string): boolean {
    return Object.values(ExtractionModel).includes(model as ExtractionModel);
  }
}
